import {
  amyQuote,
  closingSection,
  fairMarkingSection,
  firstMonthOffer,
  footer,
  liveIndexable,
  navigation,
  parentFaqs,
  practiceJourney,
  pricingSection,
  startTrial,
  topicTreeImage,
} from "./shared";
import type { LandingVariant } from "./types";

export const defaultLandingVariant = {
  slug: "default",
  path: "/",
  navigation,
  sections: [
    {
      kind: "hero",
      copy: {
        size: "display",
        pill: "GCSE & A-level exam practice",
        title: "Help your child see where they’re losing marks.",
        subtitle:
          "Exam-style questions for GCSE and A-level, marked against the official mark schemes of AQA, Edexcel, OCR and WJEC. Plus a tutor mode, timed mock exams and focus sessions. Clear feedback and a clear next step.",
        cta: startTrial,
        ctaMicrocopy: firstMonthOffer,
        image: topicTreeImage,
      },
    },
    {
      kind: "boardStrip",
      copy: {
        title: "Built around the specifications and mark schemes of",
        boards: [
          { name: "AQA", logo: "/boards/aqa.svg", width: 126, height: 73 },
          { name: "Pearson Edexcel", logo: "/boards/pearson-edexcel.svg", width: 120, height: 24 },
          { name: "OCR", logo: "/boards/ocr.svg", width: 1413, height: 363 },
          { name: "WJEC/CBAC", logo: "/boards/wjec.svg", width: 70, height: 70 },
        ],
      },
    },
    {
      kind: "story",
      copy: {
        title: "Knowing the content is not the same as getting the marks.",
        paragraphs: [
          "Many teenagers who struggle in exams know more than their results show.",
          "What they are missing is how to turn what they know into marks: structuring an answer, answering the true questions, identifying their knowledge blind spots and seeing exactly what the examiner was looking for.",
          "School feedback is often too thin to show them, and private tutors cost £35 to £85 an hour.",
        ],
        source: "Tutors' Association annual survey 2024: £35-50 an hour for GCSE, £40-85 for A-level.",
        quote: amyQuote,
      },
    },
    practiceJourney,
    {
      kind: "studyOptions",
      copy: {
        title: "Four ways to revise. One account.",
        intro:
          "Your teen can switch between them depending on what they need that day.",
        image: {
          image: "/product/session-setup.png",
          alt: "RevisionLab session setup showing Question, Tutor and Mock modes, a focus goal with Time and Marks options, and the Start question session button.",
          width: 1904,
          height: 1000,
          caption: "Demonstration data",
        },
        items: [
          {
            title: "Exam practice",
            body: "Exam-style questions, marked against the official mark scheme so they see exactly where the marks went. Revisionlab designs the questions around the areas that need it most.",
          },
          {
            title: "Tutor mode",
            body: "Work through any topic in conversation. Ask questions and get clear answers straight away.",
          },
          {
            title: "Timed mock exams",
            body: "Sit a timed paper under exam conditions and practise managing your time.",
          },
          {
            title: "Focus sessions",
            body: "Set a goal by time (10, 15 or 20 minutes) or by marks (20, 40 or 60), then keep going until you reach it.",
          },
        ],
        cta: startTrial,
      },
    },
    fairMarkingSection,
    {
      kind: "focus",
      copy: {
        eyebrow: "Focus sessions",
        title: "Short sessions that build up.",
        body: "Starting is the hard part. Focus sessions give your teen one clear task at a time, a break when they need one, and a way to build up to longer stretches at their own pace.",
        points: [
          {
            title: "Start small.",
            body: "Sessions start from 10 minutes, or set a goal of 20 marks.",
          },
          {
            title: "Breaks, never lockouts.",
            body: "A break is offered at the end of each block. They can take it or carry on.",
          },
          {
            title: "A private reflection.",
            body: "What clicked and what is still shaky stays on their device and is never shared, not even with us.",
          },
        ],
        image: {
          image: "/product/focus-complete.png",
          alt: "RevisionLab session-complete screen after a 15-minute biology session: a small tree with \"You're at Rowan\", a Focus goal met badge, 15 minutes focused, 2 questions answered and 5 marks earned, the line \"New longest block — 15 minutes\", and a private reflection note marked as staying on the student's device.",
          width: 992,
          height: 1320,
          caption: "Demonstration data",
        },
      },
    },
    {
      kind: "quotes",
      copy: {
        title: "What students and parents say.",
        quotes: [
          amyQuote,
          {
            text: "Our son is revising at the moment and he says he loves his AI tutor!! Thank you for creating RevisionLab",
            attribution: "Parent",
          },
        ],
      },
    },
    {
      kind: "comparison",
      copy: {
        title: "One month costs less than one hour with a private tutor.*",
        subtitle:
          "A tutor comes for an hour a week. RevisionLab is available everyday, at times that suit your family.",
        cta: startTrial,
        source:
          "*Tutors' Association annual survey 2024: £35-50 an hour for GCSE, £40-85 for A-level.",
      },
    },
    pricingSection,
    {
      kind: "faq",
      copy: {
        faqTitle: "Questions parents ask",
        items: parentFaqs.slice(0, 5),
        openFirst: true,
        seeAll: { label: "See all questions →", to: "/ai-faqs" },
      },
    },
    closingSection,
  ],
  footer,
  seo: {
    title: "RevisionLab — GCSE & A-level exam tutoring and practice",
    metaDescription:
      "Exam-style questions and feedback using the official specifications and mark schemes of AQA, Edexcel, OCR and WJEC.",
    canonical: "https://revisionlab.ai/",
    ogTitle: "Help your child see where they’re losing marks.",
    ogDescription:
      "Questions and feedback using the official specifications and mark schemes of AQA, Edexcel, OCR and WJEC.",
    ogImage: "/og/default.png",
  },
  publication: liveIndexable,
} as const satisfies LandingVariant;
